import React from 'react';
import { Badge } from '../ui';
import { erp } from '../content';
import { shortName } from './unit';
import { C, Explainer, Tick, type ExplainerProps, type SceneDef, type SceneProps, type Unit } from './engine';

// Lanes run row-to-row: the top five carry data in from the ERP, the bottom four carry results back.
const lanes = (u: Unit) => ({
  IN: erp.flows.in.map((label, i) => ({ label, packet: [u.parts[0]?.[1] ?? u.model, u.serial, shortName(u.shipTo, 16), 'PS-30418', 'PO-77310'][i], start: 250 + i * 620 })),
  OUT: erp.flows.out.map((label, i) => ({ label, packet: ['RMA 0412'][i], start: 3500 + i * 700 })),
});
const TRAVEL = 1500;
const X0 = 166;
const X1 = 314;
const ROW = 24;
const Y_IN = 96; // centre of first in-row
const Y_OUT = 250;
const SYNCED = 3500;

const progress = (t: number, start: number) => Math.max(0, Math.min(1, (t - start) / TRAVEL));
// gentle ease in-out so packets set off and arrive softly
const ease = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);

function Port({ lit, flash }: { lit: boolean; flash: boolean }) {
  return (
    <span style={{ position: 'relative', width: 7, height: 7, flex: 'none' }}>
      <span className="xp-abs" style={{ inset: 0, borderRadius: 7, background: lit ? C.emerald : '#fff', boxShadow: lit ? 'none' : 'inset 0 0 0 1.5px #CFCDE2', transition: 'background .2s' }} />
      {flash && <span className="xp-abs" style={{ inset: -2, borderRadius: 9, background: 'rgba(63,174,108,.5)', animation: 'xp-pulse .7s var(--xe) forwards' }} />}
    </span>
  );
}

function Panel({ side, children }: { side: 'erp' | 'bm'; children: React.ReactNode }) {
  return (
    <div className="xp-abs xp-card" style={{ left: side === 'erp' ? 16 : X1, top: 20, width: 150, height: 320, overflow: 'hidden' }}>
      {children}
    </div>
  );
}

function SectionLabel({ y, children }: { y: number; children: React.ReactNode }) {
  return (
    <div className="xp-abs xp-cap" style={{ left: 12, top: y, fontSize: 7.5, color: C.muted }}>
      {children}
    </div>
  );
}

function ErpSyncScene({ t, tone, u }: SceneProps) {
  const { IN, OUT } = lanes(u);
  const dark = tone === 'dark';
  const laneDone = (start: number) => t >= start + TRAVEL;
  const flashing = (start: number) => t >= start + TRAVEL && t < start + TRAVEL + 700;
  const departing = (start: number) => t >= start && t < start + 500;
  const rowY = (i: number, base: number) => base + i * ROW;
  const lineOn = dark ? 'rgba(207,247,16,.55)' : 'rgba(47,158,88,.55)';

  const Row = ({ label, y, side, lit, flash }: { label: string; y: number; side: 'erp' | 'bm'; lit: boolean; flash: boolean }) => (
    <div className="xp-abs xp-row" style={{ left: 0, right: 0, top: y - 20 - ROW / 2 + 1, height: ROW - 1, padding: '0 10px 0 12px', gap: 8, flexDirection: side === 'erp' ? 'row' : 'row-reverse', justifyContent: 'space-between', fontSize: 9.5, fontWeight: 500, borderTop: `1px solid ${C.line}` }}>
      <span style={{ whiteSpace: 'nowrap', paddingLeft: side === 'bm' ? 0 : 0 }}>{label}</span>
      <Port lit={lit} flash={flash} />
    </div>
  );

  return (
    <>
      {/* lanes */}
      <svg className="xp-abs" style={{ left: 0, top: 0 }} width={480} height={360}>
        {IN.map((r, i) => (
          <line key={r.label} x1={X0} x2={X1} y1={rowY(i, Y_IN)} y2={rowY(i, Y_IN)} stroke={laneDone(r.start) ? lineOn : 'var(--xp-line)'} strokeWidth="1" strokeDasharray={laneDone(r.start) ? 'none' : '2 4'} />
        ))}
        {OUT.map((r, i) => (
          <line key={r.label} x1={X0} x2={X1} y1={rowY(i, Y_OUT)} y2={rowY(i, Y_OUT)} stroke={laneDone(r.start) ? lineOn : 'var(--xp-line)'} strokeWidth="1" strokeDasharray={laneDone(r.start) ? 'none' : '2 4'} />
        ))}
      </svg>
      <div className="xp-abs xp-cap" style={{ left: X0, width: X1 - X0, top: Y_IN - 30, textAlign: 'center', fontSize: 8, color: 'var(--xp-muted)' }}>
        In →
      </div>
      <div className="xp-abs xp-cap" style={{ left: X0, width: X1 - X0, top: Y_OUT - 30, textAlign: 'center', fontSize: 8, color: 'var(--xp-muted)' }}>
        ← Back
      </div>

      <Panel side="erp">
        <div className="xp-row" style={{ height: 40, padding: '0 12px', gap: 8, background: C.alt, borderBottom: `1px solid ${C.line}` }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1.5" y="1.5" width="11" height="11" rx="2.5" stroke={C.muted} strokeWidth="1.3" />
            <path d="M1.5 5.2h11M5.2 5.2v7.3" stroke={C.muted} strokeWidth="1.3" />
          </svg>
          <span style={{ fontSize: 11.5, fontWeight: 700 }}>Your ERP</span>
          <span className="xp-mono" style={{ marginLeft: 'auto', fontSize: 7, fontWeight: 600, letterSpacing: '.1em', color: C.muted, padding: '2px 5px', borderRadius: 4, boxShadow: `inset 0 0 0 1px ${C.line}` }}>
            MASTER
          </span>
        </div>
        <SectionLabel y={Y_IN - 20 - ROW / 2 - 16}>Sends</SectionLabel>
        {IN.map((r, i) => (
          <Row key={r.label} label={r.label} y={rowY(i, Y_IN)} side="erp" lit={t >= r.start} flash={departing(r.start)} />
        ))}
        <SectionLabel y={Y_OUT - 20 - ROW / 2 - 16}>Receives</SectionLabel>
        {OUT.map((r, i) => (
          <Row key={r.label} label={r.label} y={rowY(i, Y_OUT)} side="erp" lit={laneDone(r.start)} flash={flashing(r.start)} />
        ))}
      </Panel>

      <Panel side="bm">
        <div className="xp-row" style={{ height: 40, padding: '0 12px', gap: 8, background: C.indigo, color: '#fff' }}>
          <Badge size={18} />
          <span style={{ fontSize: 11.5, fontWeight: 700 }}>BrandedMark</span>
        </div>
        <SectionLabel y={Y_IN - 20 - ROW / 2 - 16}>Reads</SectionLabel>
        {IN.map((r, i) => (
          <Row key={r.label} label={r.label} y={rowY(i, Y_IN)} side="bm" lit={laneDone(r.start)} flash={flashing(r.start)} />
        ))}
        <SectionLabel y={Y_OUT - 20 - ROW / 2 - 16}>Writes back</SectionLabel>
        {OUT.map((r, i) => (
          <Row key={r.label} label={r.label} y={rowY(i, Y_OUT)} side="bm" lit={t >= r.start} flash={departing(r.start)} />
        ))}
      </Panel>

      {/* packets */}
      {[...IN.map((r, i) => ({ ...r, y: rowY(i, Y_IN), dir: 1 })), ...OUT.map((r, i) => ({ ...r, y: rowY(i, Y_OUT), dir: -1 }))].map((r) => {
        const p = progress(t, r.start);
        const live = p > 0 && p < 1;
        const e = ease(p);
        const x = r.dir === 1 ? X0 + 4 + e * (X1 - X0 - 8) : X1 - 4 - e * (X1 - X0 - 8);
        return (
          <div
            key={r.packet}
            className="xp-abs xp-row xp-mono"
            style={{
              left: 0,
              top: r.y - 8,
              height: 16,
              padding: '0 6px',
              gap: 4,
              borderRadius: 5,
              fontSize: 7.5,
              fontWeight: 600,
              letterSpacing: '.02em',
              whiteSpace: 'nowrap',
              background: r.dir === 1 ? '#fff' : C.indigo,
              color: r.dir === 1 ? C.indigo : '#fff',
              boxShadow: r.dir === 1 ? `0 0 0 1px ${C.line}, 0 4px 10px -4px rgba(24,19,67,.3)` : dark ? '0 0 0 1px rgba(255,255,255,.25)' : '0 4px 10px -4px rgba(24,19,67,.4)',
              transform: `translateX(${x}px) translateX(-50%)`,
              opacity: live ? Math.min(1, p * 8, (1 - p) * 8) : 0,
              transition: 'transform 60ms linear, opacity 60ms linear',
              zIndex: 5,
            }}
          >
            {r.packet}
          </div>
        );
      })}

      {/* settled state */}
      <div
        className="xp-abs xp-row xp-a"
        style={{
          left: X0 + 8,
          width: X1 - X0 - 16,
          top: 30,
          justifyContent: 'center',
          gap: 5,
          fontSize: 8.5,
          fontWeight: 600,
          color: dark ? C.lime : C.emerald,
          opacity: t >= OUT[OUT.length - 1].start + TRAVEL ? 1 : 0,
          transform: t >= OUT[OUT.length - 1].start + TRAVEL ? 'none' : 'translateY(6px)',
        }}
      >
        <span className="xp-row" style={{ width: 12, height: 12, borderRadius: 12, justifyContent: 'center', background: dark ? C.lime : C.emerald }}>
          <Tick on size={8} color={dark ? C.indigo : '#fff'} />
        </span>
        In sync
      </div>
    </>
  );
}

export const erpSyncDef: SceneDef = {
  name: 'ErpSync',
  label: 'Your ERP sends parts, serials and lots, customers and dealers, packing slips and certificates to BrandedMark. BrandedMark writes cases, returns and RMAs, registrations and warranty start dates back. Your ERP stays the master.',
  W: 480,
  H: 360,
  duration: 9000,
  end: 8400,
  steps: [
    { at: 0, label: 'Read from your ERP' },
    { at: SYNCED, label: 'Written back to your ERP' },
  ],
  Scene: ErpSyncScene,
};

export const ErpSyncExplainer = (p: ExplainerProps) => <Explainer def={erpSyncDef} {...p} />;
